import React from "react";
import { decrypt } from "../utils/others";
import { StatsigFlagProvider, tierFor } from "./providers/statsig";

// The FE spelling of the cross-repo ENVIRONMENT convention: CRA only exposes
// REACT_APP_*-prefixed vars to the browser bundle.
export const environment = () =>
  process.env.REACT_APP_ENVIRONMENT || "local";

export const flagConfig = () => ({
  provider: process.env.REACT_APP_FEATURE_FLAG_PROVIDER,
  sdkKey: process.env.REACT_APP_STATSIG_CLIENT_KEY,
  environment: environment(),
});

// Same rule as the BE: the key decides. Key present → Statsig in every
// environment (the tier follows REACT_APP_ENVIRONMENT); key absent or the
// explicit REACT_APP_FEATURE_FLAG_PROVIDER=local override (e2e/CI) → offline
// local checker. An unrecognized environment also degrades to offline,
// mirroring the BE's throw-then-degrade.
export const statsigEnabled = (config) =>
  config.provider !== "local" && !!config.sdkKey && !!tierFor(config.environment);

// Re-read per call — module-scope snapshots of the user blob are a documented incident class.
// Shared FE/BE identity scheme (same person, same userID in every product):
// anonymous logged out, user_name for staff, student:<id>/parent:<id> for SSO.
// Mirrors the BE's contextFromRequest, including its priority order.
export const sessionUser = () => {
  try {
    const user = decrypt(localStorage.getItem("user"));
    if (!user) return { userID: "anonymous" };
    if (user.studentId) {
      return {
        userID: `student:${user.studentId}`,
        custom: { role: user.role, kind: "sso" },
      };
    }
    if (user.parentId) {
      return {
        userID: `parent:${user.parentId}`,
        custom: { role: user.role, kind: "sso" },
      };
    }
    if (!user.user_name) return { userID: "anonymous" };
    return {
      userID: user.user_name,
      custom: { role: user.role, kind: "staff", user_id: user.user_id },
    };
  } catch {
    return { userID: "anonymous" };
  }
};

export const FeatureFlagProvider = ({ children }) => {
  const config = flagConfig();
  if (statsigEnabled(config)) {
    return (
      <StatsigFlagProvider
        sdkKey={config.sdkKey}
        environment={config.environment}
        user={sessionUser}
      >
        {children}
      </StatsigFlagProvider>
    );
  }
  // FeatureFlagContext's default value already serves the local checker.
  return <>{children}</>;
};

export { FeatureFlag } from "./activeFlags";
export { useFeatureFlag, useFeatureFlags, useFeatureFlagsReady } from "./context";
export { FLAGS } from "./registry";
